// Login.tsx
import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { PersonOutlineRounded } from '@mui/icons-material';
import { toast } from '../../ui/toast';
import { resendInvite } from '../../utils/api_service/invite_api';
import { getOrganizationLogo } from '../../utils/api_service/organization';
import { apiUrl } from '../../utils/api_service/api_url';
import { AUTH_KEYS, NS } from '../../i18n/constants/keys';
import { TextInput } from '../../ui/input/text_input/text_input';
import { loginUser, requestPasswordChange } from '../../utils/api_service/user_api';
import { RootState } from '../../utils/redux_store/store';
import { FormInput } from '../../utils/types/login_register_types';
import { AuthenticationUiLayout } from './AuthenticationUILayout';
import { CompanyLogoBadge } from './CompanyLogoBadge';
import { ProviderIcon } from './ProviderIcon';
import { ResetPassword } from './modal_content/reset_password';
import { validate, validateForm } from './validation/validation_rules';
import { finishSignIn } from '../../utils/auth/finishSignIn';
import { setPending } from '../../utils/auth/mfaHandoff';

export const Login: React.FC = () => {
  const history = useHistory();
  const { email } = useParams<{ email: string }>();
  useEffect(() => {
    getOrganizationLogo()
      .then((r) => r.json())
      .then((d) => {
        if (d && d.auth) setSsoOptions(d.auth.ssoOptions || []);
      })
      .catch(() => {});
  }, []);
  const { t } = useTranslation(NS.auth);

  const [loginInputs, setLoginInputs] = useState<FormInput>({
    email: email || '',
    password: '',
    errors: { emailError: '', passwordError: '' },
  });
  const [formValid, setFormValid] = useState(true);
  const [ssoOptions, setSsoOptions] = useState<{ provider: string; label: string; kind: string }[]>([]);
  // Deep-link target captured by AuthenticatedRoute (?next=<in-app path>); only
  // in-app paths are honoured, never absolute/protocol-relative URLs.
  const rawNext = new URLSearchParams(window.location.search).get('next') || '';
  const deepLink =
    /^\/(?![/\\])/.test(rawNext) && !rawNext.includes('\\') && rawNext !== '/'
      ? rawNext
      : '';
  const [rememberMe, setRememberMe] = useState(true);
  const [modalState, setModalState] = useState(false);

  const companyLogo = useSelector((state: RootState) => state.logo?.companyLogo);
  const companyName = useSelector((state: RootState) => state.logo?.companyName);

  useEffect(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('email');
    localStorage.removeItem('profilePic');
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const errors = validate(e.target, loginInputs);
    setLoginInputs({
      ...loginInputs,
      [e.target.name]: e.target.value,
      errors,
    });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm(loginInputs)) {
      toast.error(t(AUTH_KEYS.login.invalidDetails), {
        position: 'bottom-center',
      });
      setFormValid(false);
      return;
    }

    loginUser(loginInputs)
      .then((res) => {
        if (!res.success) {
          if (res.error === 'Not Verified') {
            resendInvite(loginInputs.email);
            toast.info('Your account is not set up yet. We have re-sent your invitation email.',
              { position: 'bottom-center' });
          } else {
            toast.error(res.error, { position: 'bottom-center' });
          }
          return;
        }
        if (rememberMe) {
          localStorage.setItem('email', loginInputs.email);
        }
        // MFA gates: route to the challenge / enrolment step with the token.
        // The deep link is stored with the pending MFA step so the second
        // factor still lands the user where they were headed.
        if (res.mfaRequired && res.challengeToken) {
          setPending('challenge', res.challengeToken, deepLink);
          history.push('/auth/mfa');
          return;
        }
        if (res.mfaEnrollmentRequired && res.enrollToken) {
          setPending('enroll', res.enrollToken, deepLink);
          history.push('/auth/mfa/enroll');
          return;
        }

        finishSignIn(res, { deepLink }).then((target) => history.push(target));
      })
      .catch((err) => toast.error(err, { position: 'bottom-center' }));
  };

  const sendPasswordResetMail = (e: FormEvent, resetEmail: string) => {
    e.preventDefault();
    requestPasswordChange(resetEmail).then((res) => {
      if (res.success) {
        toast.success(t(AUTH_KEYS.login.resetMailSent), {
          position: 'bottom-center',
        });
        setModalState(false);
      } else {
        toast.error(res.error, { position: 'bottom-center' });
      }
    });
  };

  return (
    <AuthenticationUiLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        {/* Top Header Badge or User Avatar Icon */}
        {companyName || companyLogo ? (
          <Box sx={{ mb: 2 }}>
            <CompanyLogoBadge
              logoUrl={companyLogo}
              companyName={companyName}
              variant='dark-glass'
              altText='Company Logo'
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: '#f1f5f9',
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <PersonOutlineRounded sx={{ fontSize: 26 }} />
          </Box>
        )}

        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: '1.5rem', sm: '1.875rem' },
            fontWeight: 800,
            color: '#0f172a',
            letterSpacing: '-0.02em',
            textAlign: 'center',
          }}
        >
          {t(AUTH_KEYS.login.signIn) || 'Welcome back!'}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            textAlign: 'center',
            mt: 0.5,
            mb: 3,
          }}
        >
          {companyName ? `Sign in to access ${companyName}` : 'Enter your login details'}
        </Typography>

        {/* Login Form */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          data-testid="login-form"
          sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {/* Email Input */}
          <TextInput
            type="email"
            label={t(AUTH_KEYS.login.email) || 'Email'}
            name="email"
            variant="pill"
            placeholder="Enter your email"
            value={loginInputs.email}
            onChange={handleChange}
            required
            error={!formValid && Boolean(loginInputs.errors.emailError)}
            helperText={!formValid ? loginInputs.errors.emailError : undefined}
          />

          {/* Password Input */}
          <TextInput
            type="password"
            label={t(AUTH_KEYS.login.password) || 'Password'}
            name="password"
            variant="pill"
            placeholder="Enter your password"
            value={loginInputs.password}
            onChange={handleChange}
            required
            error={!formValid && Boolean(loginInputs.errors.passwordError)}
            helperText={!formValid ? loginInputs.errors.passwordError : undefined}
          />

          {/* Checkbox and Forgot Password Row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 0.5,
              px: 0.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  size="small"
                  sx={{
                    color: '#94a3b8',
                    p: 0.5,
                    '&.Mui-checked': {
                      color: '#059669',
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#334155', userSelect: 'none' }}>
                  Remember me
                </Typography>
              }
            />

            <Typography
              component="button"
              type="button"
              onClick={() => setModalState(true)}
              sx={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                p: 0,
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#334155',
                transition: 'color 0.2s',
                '&:hover': {
                  color: '#059669',
                  textDecoration: 'underline',
                },
              }}
            >
              {t(AUTH_KEYS.login.forgotPassword) || 'Forgot password?'}
            </Typography>
          </Box>

          {/* Main CTA Button: Black rounded pill button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 1.5,
              py: 1.5,
              px: 3,
              borderRadius: '9999px',
              bgcolor: '#050508',
              color: '#ffffff',
              fontSize: '0.875rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 14px rgba(0, 0, 0, 0.16)',
              '&:hover': {
                bgcolor: '#1e293b',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.22)',
              },
            }}
          >
            {t(AUTH_KEYS.login.loginButton) || 'Log in'}
          </Button>

          {ssoOptions.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }}>
                <Typography
                  sx={{
                    px: 1.5,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Or
                </Typography>
              </Divider>

              <Stack spacing={1.25}>
                {ssoOptions.map((opt) => (
                  <Button
                    key={opt.provider}
                    type="button"
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      const returnTo = encodeURIComponent(window.location.origin);
                      const next = encodeURIComponent(deepLink || '/');
                      window.location.href = `${apiUrl}/auth/sso/start?returnTo=${returnTo}&provider=${encodeURIComponent(opt.provider)}&next=${next}`;
                    }}
                    startIcon={<ProviderIcon provider={opt.provider} />}
                    sx={{
                      py: 1.25,
                      px: 3,
                      borderRadius: '9999px',
                      borderColor: '#e2e8f0',
                      color: '#1e293b',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#cbd5e1',
                        bgcolor: '#f8fafc',
                      },
                    }}
                  >
                    {opt.label || 'Continue with SSO'}
                  </Button>
                ))}
              </Stack>
            </>
          )}
        </Box>

        <ResetPassword
          open={modalState}
          onClose={() => setModalState(false)}
          handleSubmit={sendPasswordResetMail}
        />
      </Box>
    </AuthenticationUiLayout>
  );
};
